import { Theme, getFontName } from "@/theme"
import * as Geo from "./geo"

import type { LayoutChangeEvent } from "react-native"
import type { EdgePadding, LatLng, Region } from "react-native-maps"
import type { SelectedItem } from "@/redux/status-map.reducer"

export type LayoutChangeHandler = (event: LayoutChangeEvent) => void

export const pointTransformerNative: Geo.PointTransformer<LatLng> = (pt) =>
  Geo.point(pt)?.toNative()

// Function to check if a coordinate is in the current region
export function isCoordinateInRegion({
  coordinate,
  region,
}: {
  coordinate: LatLng | null | undefined
  region: Region | null | undefined
}) {
  if (!region || !coordinate) {
    return false
  }

  const { latitude, latitudeDelta, longitude, longitudeDelta } = region
  const { latitude: markerLat, longitude: markerLng } = coordinate

  return (
    markerLat >= latitude - latitudeDelta / 2 &&
    markerLat <= latitude + latitudeDelta / 2 &&
    markerLng >= longitude - longitudeDelta / 2 &&
    markerLng <= longitude + longitudeDelta / 2
  )
}

export function parseDeviceIdFromMarkerId(markerId: string): string {
  return markerId.substring("device-".length)
}

// Function to check if a coordinate is in the current bounds
export function isCoordinateInBounds({
  bounds,
  coordinate,
}: {
  bounds: google.maps.LatLngBoundsLiteral | null | undefined
  coordinate: google.maps.LatLngLiteral | null | undefined
}): boolean {
  if (!bounds || !coordinate) {
    return false
  }

  const { east, north, south, west } = bounds
  const { lat: markerLat, lng: markerLng } = coordinate

  return (
    markerLat >= south &&
    markerLat <= north &&
    markerLng >= west &&
    markerLng <= east
  )
}

export const pointTransformerWeb: Geo.PointTransformer<
  google.maps.LatLngLiteral
> = (pt) => Geo.point(pt)?.toGmaps()

export const MARKER_LABEL_OPTIONS: Omit<google.maps.MarkerLabel, "text"> = {
  color: Theme.COLORS.$textLight.primary,
  fontFamily: getFontName("OpenSans_400Regular"),
  fontSize: `${Theme.FONT_SIZES.$sm}px`,
}
export type EdgePaddingInput =
  | Partial<{
      [key in keyof EdgePadding]: number
    }>
  | number
  | null

export function getEdgePadding(padding?: EdgePaddingInput): EdgePadding {
  let bottom = 0
  let left = 0
  let right = 0
  let top = 0

  if (typeof padding === "number") {
    bottom = padding
    left = padding
    right = padding
    top = padding
  } else if (padding) {
    bottom = padding.bottom ?? bottom
    left = padding.left ?? left
    right = padding.right ?? right
    top = padding.top ?? top
  }

  return { bottom, left, right, top }
}

export type MapMarkerIdentifier =
  | `${`deviceConnection` | `field` | `reelRun`}-${number}`
  | `active-farm`
  | `device-${string}`
  | `deviceActivity-${string}`

/**
 * Create a map marker id string
 */
export function makeMapMarkerId(
  item: SelectedItem | "active-farm",
): MapMarkerIdentifier {
  if (item === "active-farm") {
    return item
  }
  if (item.kind === "device" || item.kind === "deviceActivity") {
    return `${item.kind}-${item.id}`
  }
  // TODO: fix type casting
  return `${item.kind}-${item.id}` as MapMarkerIdentifier
}
/**
 * Extract field id from map marker id string
 */
export function parseFieldId(id: string) {
  return Number.parseInt(id.substring("field-".length))
}
/**
 * Extract run id from map marker id string
 */
export function parseReelRunId(id: string) {
  return Number.parseInt(id.substring("reelRun-".length))
}
